import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getEmailCampaign,
  getEmailCampaignAudiences,
  getEmailCampaignRecipients,
} from '../../endpoints/emailCampaigns';
import type {
  Audience,
  AudienceCriteria,
  EmailCampaign,
} from '../../endpoints/emailCampaigns';
import { getGroupTypes, getGroupRolesByType } from '../../endpoints/groups';
import { GroupRole, GroupType } from '../../types/group';
import AlertItem from '../../Components/AlertItem';
import TablePeo from '../../Components/TablePeo';
import { exportToExcel } from '../../utils/exportUtils';
import type { SortOrder } from '../../types/table';
import dayjs from 'dayjs';

const RECIPIENT_HEAD_CELLS = [
  { id: 'last_name', label: 'Last name', firstCell: true, sortable: false },
  { id: 'first_name', label: 'First name', sortable: false },
  { id: 'email', label: 'Email', sortable: false },
];

const useAudienceSummary = (criteria?: AudienceCriteria | null) => {
  const { data: internationalGroups = [] } = useApiGet<GroupType[]>(
    ['group-types', 'international', 'email-campaign-detail'],
    () => getGroupTypes('international'),
    { enabled: !!criteria?.criteria?.length }
  );
  const { data: spdGroups = [] } = useApiGet<GroupType[]>(
    ['group-types', 'spd_chapter', 'email-campaign-detail'],
    () => getGroupTypes('spd_chapter'),
    { enabled: !!criteria?.criteria?.length }
  );
  const { data: localGroups = [] } = useApiGet<GroupType[]>(
    ['group-types', 'local_chapter', 'email-campaign-detail'],
    () => getGroupTypes('local_chapter'),
    { enabled: !!criteria?.criteria?.length }
  );
  const { data: legacyAudiences = [] } = useApiGet<Audience[]>(
    ['email-campaign-audiences', 'detail'],
    getEmailCampaignAudiences,
    { enabled: !!criteria?.legacy_audience_ids?.length }
  );

  const groupNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of [...internationalGroups, ...spdGroups, ...localGroups]) {
      map.set(g.id, g.name);
    }
    return map;
  }, [internationalGroups, spdGroups, localGroups]);

  const legacyNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of legacyAudiences) map.set(a.id, a.name);
    return map;
  }, [legacyAudiences]);

  return { criteria, groupNameById, legacyNameById };
};

const CriterionSummary: React.FC<{
  criterion: AudienceCriteria['criteria'][number];
  groupName: string;
}> = ({ criterion, groupName }) => {
  const { data: roles = [] } = useApiGet<GroupRole[]>(
    ['group-roles', criterion.group_type_id, 'email-campaign-detail'],
    () => getGroupRolesByType(criterion.group_type_id),
    { enabled: !criterion.all_roles }
  );

  let roleLabel = 'All roles';
  if (!criterion.all_roles && criterion.role_ids?.length) {
    const names = criterion.role_ids
      .map((id) => roles.find((r) => r.id === id)?.name || id)
      .join(', ');
    roleLabel = names || `${criterion.role_ids.length} role(s)`;
  }

  return (
    <Typography variant="body2" sx={{ mb: 0.5 }}>
      {groupName} — {roleLabel}
    </Typography>
  );
};

const EmailCampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [recipientPage, setRecipientPage] = useState(0);
  const [recipientRowsPerPage, setRecipientRowsPerPage] = useState(25);
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState('last_name');
  const [recipientItems, setRecipientItems] = useState<
    { id: string; last_name: string; first_name: string; email: string }[]
  >([]);
  const [recipientTotal, setRecipientTotal] = useState(0);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const {
    data: campaign,
    isLoading,
    error,
  } = useApiGet<EmailCampaign>(
    ['email-campaign', id!],
    () => getEmailCampaign(id!),
    { enabled: !!id, retry: 1 }
  );

  const { groupNameById, legacyNameById } = useAudienceSummary(
    campaign?.audience_criteria
  );

  const showRecipients = campaign?.status === 'sent';

  useEffect(() => {
    if (!id || !showRecipients) return;
    let cancelled = false;
    setRecipientsLoading(true);
    getEmailCampaignRecipients(id, recipientPage + 1, recipientRowsPerPage)
      .then((res) => {
        if (cancelled) return;
        setRecipientItems(
          res.items.map((r, i) => ({
            id: r.user_id || r.email || String(i),
            last_name: r.last_name || '—',
            first_name: r.first_name || '—',
            email: r.email,
          }))
        );
        setRecipientTotal(res.total_count);
      })
      .catch(() => {
        if (!cancelled) {
          setRecipientItems([]);
          setRecipientTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setRecipientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, showRecipients, recipientPage, recipientRowsPerPage]);

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    try {
      return dayjs(dateString).format('MMM DD, YYYY h:mm A');
    } catch {
      return '—';
    }
  };

  const audienceSummary = () => {
    const ac = campaign?.audience_criteria;
    if (ac?.criteria?.length || ac?.legacy_audience_ids?.length) {
      return (
        <Box>
          {(ac.criteria ?? []).map((c) => (
            <CriterionSummary
              key={c.group_type_id}
              criterion={c}
              groupName={groupNameById.get(c.group_type_id) || c.group_type_id}
            />
          ))}
          {(ac.legacy_audience_ids ?? []).map((lid) => (
            <Typography key={lid} variant="body2" sx={{ mb: 0.5 }}>
              {legacyNameById.get(lid) || lid}
            </Typography>
          ))}
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.5 }}
          >
            Active members only:{' '}
            {ac.active_members_only !== false ? 'Yes' : 'No'}
          </Typography>
        </Box>
      );
    }
    return (
      <Typography variant="body2">
        {campaign?.audience_names ?? campaign?.audience_ids ?? '—'}
      </Typography>
    );
  };

  if (!id) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Grid item xs={12} padding={2}>
          <Typography color="error">Missing campaign ID.</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/administration/email-campaigns')}
          >
            Back to list
          </Button>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Grid item xs={12} padding={2}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error || !campaign) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Grid item xs={12} padding={2}>
          <AlertItem
            severity="error"
            description="Campaign not found or failed to load."
            onClose={() => {}}
            keepOpen
          />
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/administration/email-campaigns')}
          >
            Back to list
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/administration/email-campaigns')}
          sx={{ mb: 2 }}
        >
          Back to list
        </Button>
        <Typography variant="h4" color="primary">
          {campaign.name}
        </Typography>
        <Chip
          label={campaign.status}
          size="small"
          sx={{ mt: 1 }}
          color="primary"
          variant="outlined"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Details
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell variant="head" width="180">
                  Template ID
                </TableCell>
                <TableCell>{campaign.template_id || '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Subject</TableCell>
                <TableCell>{campaign.subject || '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Audiences</TableCell>
                <TableCell>{audienceSummary()}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Estimated recipients</TableCell>
                <TableCell>
                  {campaign.estimated_recipient_count ?? '—'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Recipient count</TableCell>
                <TableCell>{campaign.recipient_count ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Scheduled at</TableCell>
                <TableCell>{formatDateTime(campaign.scheduled_at)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Sent at</TableCell>
                <TableCell>{formatDateTime(campaign.sent_at)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Created at</TableCell>
                <TableCell>{formatDateTime(campaign.created_at)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            SendGrid stats
          </Typography>
          {campaign.last_stats_at ? (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Last updated: {formatDateTime(campaign.last_stats_at)}
            </Typography>
          ) : null}
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell variant="head" width="180">
                  Sent
                </TableCell>
                <TableCell>{campaign.sent_count ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Delivered</TableCell>
                <TableCell>{campaign.delivered_count ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Opened</TableCell>
                <TableCell>{campaign.opened_count ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Clicked</TableCell>
                <TableCell>{campaign.clicked_count ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell variant="head">Bounces</TableCell>
                <TableCell>{campaign.bounce_count ?? '—'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {!campaign.last_stats_at && (
            <Typography variant="body2" color="text.secondary">
              Stats are updated by a daily sync from SendGrid.
            </Typography>
          )}
        </Paper>
      </Grid>

      {showRecipients && (
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recipients
            </Typography>
            <TablePeo
              data={recipientItems}
              headCells={RECIPIENT_HEAD_CELLS}
              isFetching={recipientsLoading}
              page={recipientPage}
              setPage={setRecipientPage}
              rowsPerPage={recipientRowsPerPage}
              setRowsPerPage={setRecipientRowsPerPage}
              order={order}
              setOrder={setOrder}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              pagination
              remoteTotalCount={recipientTotal}
              emptyMessage="No recipients recorded."
              size="small"
              toolbar={
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!recipientItems.length}
                  onClick={() =>
                    exportToExcel(
                      recipientItems,
                      [
                        { field: 'last_name', headerName: 'Last name' },
                        { field: 'first_name', headerName: 'First name' },
                        { field: 'email', headerName: 'Email' },
                      ],
                      { filename: `email-campaign-${campaign.id}-recipients` }
                    )
                  }
                >
                  Export Excel
                </Button>
              }
            />
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default EmailCampaignDetail;
